import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Avatar, Box, CircularProgress, IconButton, Slider, Stack, Tooltip, Typography} from "@mui/material";
import moment from "moment";
import {useAtom, useSetAtom} from "jotai";
import {SelectedConversationAudioPlayer, SelectedMessageAudioPlayer} from "../atoms";
import {SignInContext} from "../../../../context/SignInContext";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {createAudioLoader, ensureAudioSource, isAbortError} from "../../../../utils/audioLoader";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Cancel01Icon, CheckIcon, EyeOffIcon, EyeIcon, MusicNote01Icon, PauseIcon, PencilEdit01Icon, PlayIcon, ViewIcon
} from "@hugeicons/core-free-icons";

import {formatTime} from "../../../../utils/functions";
import {DebugMessageModal} from "./debug-message";
import {MessageAnnotationsRepository} from "../../../../repositories/MessageAnnotationRepository";
import {toast} from "react-toastify";
import {ProjectRoles} from "../../../../context/AppContext";

const processingStatuses = ["Processing speech", "Transcribing audio", "Finalizing text"];

// An agent message without text that puts the caller on hold music (moh = music on hold)
export const isMohMessage = (message) => (message?.role || message?.sender) === "AGENT"
  && (message?.moh === true || message?.moh === "true")
  && !message?.text?.trim();

// Shown outside the bubbles while the hold music plays (a music-on-hold message is the latest message)
const MusicOnHold = () => (
  <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1.25} role={"status"}
         aria-label={"Music on hold is playing"} sx={{mx: "auto", my: 1, color: "primary.main"}}>
    <Box sx={{
      width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: "rgba(132, 145, 247, 0.14)",
      animation: "mohPulse 1.8s ease-in-out infinite",
      "@keyframes mohPulse": {
        "0%, 100%": {boxShadow: "0 0 0 0 rgba(132, 145, 247, 0.35)"},
        "50%": {boxShadow: "0 0 0 6px rgba(132, 145, 247, 0)"},
      },
      "@media (prefers-reduced-motion: reduce)": {animation: "none"},
    }}>
      <HugeiconsIcon icon={MusicNote01Icon} size={16}/>
    </Box>
    <Stack direction={"row"} alignItems={"flex-end"} gap={0.375} sx={{height: 16}} aria-hidden>
      {[0, 0.25, 0.5, 0.15].map((delay, index) => (
        <Box key={index} sx={{
          width: 3, height: "100%", borderRadius: 1, bgcolor: "primary.main", transformOrigin: "bottom",
          animation: `mohBar 0.9s ease-in-out ${delay}s infinite`,
          "@keyframes mohBar": {"0%, 100%": {transform: "scaleY(0.3)"}, "50%": {transform: "scaleY(1)"}},
          "@media (prefers-reduced-motion: reduce)": {animation: "none", transform: "scaleY(0.6)"},
        }}/>
      ))}
    </Stack>
    <Box>
      <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: "inherit"}}>Music on hold</Typography>
      <Typography sx={{fontSize: 11, lineHeight: 1.3, color: "text.secondary"}}>The caller is listening to hold music</Typography>
    </Box>
  </Stack>
);

// conversationId: conversation of the message, for its audio (GET /api/conversation/{id}/messages/{messageId}/audio)
// mohPlaying: a music-on-hold message that is still the latest message (see isMohMessage); one that is not renders
// nothing - the hold music has stopped
export const MessageBox = ({
                             message,
                             conversationId,
                             prevMessage,
                             project,
                             conversationType,
                             hideStats = false,
                             showDebug = true,
                             mohPlaying = false,
                             setAgentTalking = () => {
                             },
                             fetchMessages = () => {
                             }
                           }) => {
  const [debugMessage, setDebugMessage] = useState(null);
  const [selectedMessage, setSelectedMessage] = useAtom(SelectedMessageAudioPlayer);
  const [isTalking, setIsTalking] = useState(false);
  const setSelectedConversation = useSetAtom(SelectedConversationAudioPlayer);

  const {currentUser} = useContext(SignInContext)

  const [isEditMode, setIsEditMode] = useState(false);
  const [annotationText, setAnnotationText] = useState("");
  const [showOriginal, setShowOriginal] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const editableRef = useRef(null);

  const audioRef = useRef(null);
  const audioLoaderRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);

  // Audio of the message. The DTO does not say whether there is one, so it is downloaded (as a blob, it needs the
  // Authorization header) when the message is clicked; a message without audio shows a toast instead.
  useEffect(() => {
    if (!message?.id || !conversationId) return;

    const audio = new Audio();
    audio.preload = "metadata";
    const loader = createAudioLoader(({signal}) => ConversationRepository.getMessageAudio({
      conversationId, messageId: message.id, flowUid: project?.id, signal,
    }));

    audioRef.current = audio;
    audioLoaderRef.current = loader;

    const handleMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setSelectedMessage(prev => ({...prev, currentTime: audio.currentTime}));
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.removeEventListener("loadedmetadata", handleMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      loader.dispose();
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [message?.id, conversationId]);

  // Pause if another message is selected
  useEffect(() => {
    if (selectedMessage?.id !== message?.id && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [selectedMessage]);

  useEffect(() => {
    if (message?.role !== "AGENT" || !message?.audio_start || !message?.audio_end) return;

    const messageStart = message?.audio_start ? new Date(message?.audio_start).getTime() : null; // timestamp
    const messageEnd = message?.audio_end ? new Date(message?.audio_end).getTime() : null; // timestamp
    // console.log(message?.text, duration);
    const now = new Date().getTime();
    // console.log(message?.text, now >= messageStart && now <= messageEnd, messageStart, messageEnd, now)

    if (now >= messageStart && now <= messageEnd) {
      // It's within the talking window
      setIsTalking(true);
      setAgentTalking?.(message, false)
      // console.log("", message?.id)
      const msUntilEnd = messageEnd - now;

      const timeout = setTimeout(() => {
        setIsTalking(false);
        setAgentTalking?.(message, false);
      }, msUntilEnd);

      return () => clearTimeout(timeout);
    } else if (now <= messageStart) {
      //   Here write script
      const msUntilStart = now - messageStart;
      const startTimeout = setTimeout(() => {
        setIsTalking(true);
        setAgentTalking?.(message, true);
      }, msUntilStart);
      const msUntilEnd = messageEnd - now;
      const endTimeout = setTimeout(() => {
        setIsTalking(false);
        setAgentTalking?.(message, false);
      }, msUntilEnd);

      return () => {
        clearTimeout(startTimeout);
        clearTimeout(endTimeout);
      }
    }
  }, [message?.id]);


  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || audioLoading) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setSelectedMessage({...message, duration: duration});
      setSelectedConversation(null); // stop conversation-level audio
      setAudioLoading(true);
      ensureAudioSource(audio, audioLoaderRef.current)
        .then(ready => ready && audio.play())
        .then(played => played !== false && setIsPlaying(true))
        .catch(error => {
          if (isAbortError(error)) return;
          if (error?.response?.status === 404) {
            toast.info("This message has no audio");
          } else {
            console.error("Failed to play message audio:", error);
            toast.error("Failed to load the message audio");
          }
          setSelectedMessage(current => current?.id === message?.id ? null : current);
        })
        .finally(() => setAudioLoading(false));
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const {role, isPrev, isSelected} = useMemo(() => {
    const isPrev = message?.role !== prevMessage?.role;
    const role = message?.role || message?.sender;
    const isSelected = selectedMessage?.id === message.id;
    return {role, isPrev, isSelected};
  }, [message, prevMessage, selectedMessage]);

  // A USER message with protection still set to false is being processed by
  // the backend. Keep the text visible, but give the bubble a subtle live state.
  const isProcessingUserMessage = conversationType === "VOICE_BOT"
    && role === "USER"
    && (message?.is_protected === false || message?.is_protected === "false");

  useEffect(() => {
    if (!isProcessingUserMessage) {
      setProcessingStage(0);
      return undefined;
    }

    setProcessingStage(0);
    const interval = setInterval(() => {
      setProcessingStage(previous => {
        if (previous >= processingStatuses.length - 1) {
          clearInterval(interval);
          return previous;
        }
        return previous + 1;
      });
    }, 1400);

    return () => clearInterval(interval);
  }, [isProcessingUserMessage, message?.id]);

  const audioPlayer = selectedMessage?.id === message?.id ? (
    <Stack direction={"row"} alignItems="center" justifyContent={"center"} gap={1}>
      <IconButton size="small" onClick={togglePlay} disabled={audioLoading}>
        {audioLoading
          ? <CircularProgress size={14}/>
          : isPlaying ? <HugeiconsIcon icon={PauseIcon} size={16}/> : <HugeiconsIcon icon={PlayIcon} size={16} strokeWidth={2}/>}
      </IconButton>
      <Typography variant="body2" minWidth={35}>
        {formatTime(currentTime)}
      </Typography>
      <Slider
        color="secondary"
        size="small"
        min={0}
        max={duration}
        step={0.1}
        value={currentTime}
        onChangeCommitted={(e, value) => handleSeek(value)}
        sx={{flexGrow: 1, maxWidth: "330px", minWidth: "150px"}}
      />
      <Typography variant="body2" minWidth={35}>
        {formatTime(duration)}
      </Typography>
      <IconButton size="small"
                  onClick={() => {
                    setSelectedMessage(null);
                    setSelectedConversation(null);
                  }}
      >
        <HugeiconsIcon icon={Cancel01Icon} size={16}/>
      </IconButton>
    </Stack>
  ) : null;

  const contentStyles = {
    cursor: "pointer",
    p: 1.5,
    borderRadius: 5,
    boxShadow: isSelected ? 5 : 0,
    minWidth: "100px"
  };

  if (isMohMessage(message)) {
    return mohPlaying ? <MusicOnHold/> : null;
  }

  if (role === "AGENT" && (message?.text || showDebug)) {
    return (
      <Stack className="agent-msg" direction="column" alignItems="flex-start" gap={1} maxWidth="md"
             sx={{mx: "auto", width: "100%"}}>
        <Stack
          direction="column"
          maxWidth="sm"
          onClick={togglePlay}
          sx={{
            ...contentStyles,
            mt: isPrev ? 0.5 : 0,
            bgcolor: isTalking || message?.last_message ? "primary.dark" : "primary.main",
            color: "#FFF",
            borderTopLeftRadius: 0
          }}
        >
          <Typography fontSize={12} fontFamily="Inter" color="inherit" gutterBottom>
            {message?.text || <em>Last message...</em>}
            <br/>
          </Typography>
          {!hideStats && <MessageFooter message={message} color="#FFF"/>}
        </Stack>
        {audioPlayer}
      </Stack>
    );
  }

  if (role === "USER" && (message?.text || showDebug)) {

    const handleEditClick = () => {
      setAnnotationText(message?.text || "");
      setIsEditMode(true);
    };

    const handleCancelEdit = () => {
      setIsEditMode(false);
      setAnnotationText("");
    };

    const handleConfirmAnnotation = () => {
      const text = editableRef.current?.textContent || annotationText;
      MessageAnnotationsRepository.createOrUpdate({
        messageId: message?.id, annotatedText: text
      })
        .then(() => fetchMessages())
        .catch(error => toast?.error(error?.message || "Failed to annotate"))
        .finally(() => setIsEditMode(false));
    };

    const handleToggleView = () => {
      setShowOriginal(!showOriginal);
    };

    const displayedText = message?.is_annotated && !showOriginal
      ? message?.annotatedText
      : message?.text || <em>User not talking...</em>;

    const member = project?.members?.find(i => i?.username === currentUser?.email || i?.username === currentUser?.username);
    console.log(member, project)
    return (
      <Stack
        id={`message-${message?.id}`}
        direction={"column"} maxWidth={"md"} gap={0.5}
        alignItems={"flex-end"}
        sx={{px: 1, ml: "auto", width: "100%"}}
      >
        <Stack
          direction={"row"} gap={2}
          alignItems={"center"} justifyContent={"flex-end"}
          sx={{
            width: "100%",
            "&:hover > .msg-buttons": {
              opacity: 1
            }
          }}
        >
          <Box className={"msg-buttons"}
               sx={{display: "flex", justifyContent: "flex-end", minWidth: "60px", opacity: isEditMode ? 1 : 0}}>
            {
              member?.role === ProjectRoles.ANNOTATOR ?
                isEditMode ? (
                  <Stack direction={"row"} gap={0.5}>
                    <IconButton size={"small"} color="success" onClick={handleConfirmAnnotation}>
                      <HugeiconsIcon icon={CheckIcon} size={18}/>
                    </IconButton>
                    <IconButton size={"small"} color="error" onClick={handleCancelEdit}>
                      <HugeiconsIcon icon={Cancel01Icon} size={18}/>
                    </IconButton>
                  </Stack>
                ) :
                  <>
                    <IconButton size={"small"} disabled={!message?.text} onClick={handleEditClick}>
                      <HugeiconsIcon icon={PencilEdit01Icon} size={18}/>
                    </IconButton>
                    {message?.is_annotated ? (
                    <Tooltip
                      title={message?.is_annotated ? (showOriginal ? "Original Text" : "Annotated Text") : ""}
                      arrow
                      placement="top"
                    >
                      <IconButton size={"small"} onClick={handleToggleView}>
                        {showOriginal ? <HugeiconsIcon icon={ViewIcon} size={18}/> : <HugeiconsIcon icon={EyeOffIcon} size={18}/>}
                      </IconButton>
                    </Tooltip>
                    ): null}
                  </>
                :
                (
                  <Tooltip
                    title={message?.is_annotated ? (showOriginal ? "Original Text" : "Annotated Text") : ""}
                    arrow
                    placement="top"
                  >
                    <IconButton size={"small"} onClick={handleToggleView}>
                      {showOriginal ? <HugeiconsIcon icon={ViewIcon} size={18}/> : <HugeiconsIcon icon={EyeOffIcon} size={18}/>}
                    </IconButton>
                  </Tooltip>
                )
            }
          </Box>

          <Stack className="user-msg" direction="column" alignItems="flex-end" gap={1} maxWidth="md">
            <Stack
              direction="column"
              maxWidth="sm"
              onClick={togglePlay}
              sx={{
                ...contentStyles,
                mt: isPrev ? 0.5 : 0,
                bgcolor: "#EAEAEA",
                color: "dark.main",
                borderTopRightRadius: 0,
                position: "relative",
                overflow: "hidden",
                minWidth: isProcessingUserMessage ? "160px" : undefined,
                ...(isProcessingUserMessage && {
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: "linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.75) 45%, transparent 70%)",
                    transform: "translateX(-120%)",
                    animation: "messageShimmer 2.2s ease-in-out infinite",
                  },
                  "@keyframes messageShimmer": {
                    "0%": {transform: "translateX(-120%)"},
                    "55%, 100%": {transform: "translateX(120%)"},
                  },
                }),
              }}
            >
              <Tooltip
                title={message?.is_annotated && !isEditMode ? (showOriginal ? "Original Text" : "Annotated Text") : ""}
                arrow
                placement="top"
              >
                <Typography
                  ref={editableRef}
                  contentEditable={isEditMode}
                  suppressContentEditableWarning
                  color={"inherit"}
                  fontSize={12}
                  // fontWeight={500}
                  onClick={(e) => isEditMode && e.stopPropagation()}
                  onBlur={(e) => isEditMode && setAnnotationText(e.currentTarget.textContent)}
                  sx={isEditMode ? {
                    outline: "none",
                    minWidth: 100,
                    cursor: "text"
                  } : {minWidth: 100}}
                  fontFamily="Inter"
                  gutterBottom
                >
                  {isProcessingUserMessage
                    ? (
                      <Box
                        component="span"
                        key={processingStage}
                        sx={{
                          display: "inline-block",
                          animation: "messageStatusIn 420ms cubic-bezier(0.22, 1, 0.36, 1)",
                          "@keyframes messageStatusIn": {
                            "0%": {opacity: 0, transform: "translateY(5px) blur(2px)"},
                            "100%": {opacity: 1, transform: "translateY(0) blur(0)"},
                          },
                        }}
                      >
                        {processingStatuses[processingStage]}
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{display: "inline-flex", ml: 0.35, gap: "2px", verticalAlign: "baseline"}}
                        >
                          {[0, 1, 2].map(index => (
                            <Box
                              component="span"
                              key={index}
                              sx={{
                                display: "inline-block",
                                width: 3,
                                height: 3,
                                borderRadius: "50%",
                                bgcolor: "currentColor",
                                transform: "translateY(1px)",
                                animation: "messageDotJump 900ms ease-in-out infinite",
                                animationDelay: `${index * 140}ms`,
                                "@keyframes messageDotJump": {
                                  "0%, 60%, 100%": {transform: "translateY(1px)", opacity: 0.35},
                                  "30%": {transform: "translateY(-3px)", opacity: 1},
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )
                    : (isEditMode ? annotationText : displayedText) ||
                      <em>The user is not speaking, or is speaking unclearly.</em>}
                </Typography>
              </Tooltip>
              {!hideStats && <MessageFooter message={message} color="dark.main"/>}
            </Stack>
            {audioPlayer}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (hideStats) return null;
  if (!showDebug) return null;

  return (
    <>
      <DebugMessageModal
        open={!!debugMessage}
        handleClose={() => setDebugMessage(null)}
        debugMessage={debugMessage}
      />
      <Stack
        onClick={() => setDebugMessage(message)}
        direction="column"
        maxWidth="sm"
        justifyContent="center"
        sx={{
          p: 1.5,
          mx: "auto",
          width: "90%",
          minWidth: "100px",
          color: "dark.main",
          bgcolor: "#f1f1f1ee",
          borderRadius: 5,
          cursor: "pointer"
        }}
      >
        <Typography fontSize={12} fontWeight={600}>Debug Message</Typography>
        <Typography fontSize={12} fontFamily="Inter" sx={{borderLeft: 2, borderColor: "divider", pl: 1}}>
          {message?.text?.slice(0, 300)} {message?.text?.length > 300 && "..."}
        </Typography>
        {!hideStats && <MessageFooter message={message} color="dark.main"/>}
      </Stack>
    </>

  );
};

const MessageFooter = ({message, color}) => (
  <Stack direction="row" gap={1} sx={{color: color || "inherit", width: "95%"}}>
    {message?.responseTime?.chatbot && (
      <Typography fontSize={10}
                  sx={{textWrap: "nowrap"}}>Chatbot: {(message.responseTime.chatbot / 1000).toFixed(2)}s</Typography>
    )}
    {message?.responseTime?.transcribe && (
      <Typography fontSize={10}
                  sx={{textWrap: "nowrap"}}>Transcribe: {(message.responseTime.transcribe / 1000).toFixed(2)}s</Typography>
    )}
    {message?.responseTime?.synthesize && (
      <Typography fontSize={10}
                  sx={{textWrap: "nowrap"}}>Synthesize: {(message.responseTime.synthesize / 1000).toFixed(2)}s</Typography>
    )}
    <Typography sx={{ml: "auto", textWrap: "nowrap"}} fontSize={10}>
      {moment(message?.dateCreated).format("HH:mm:ss")}
    </Typography>
  </Stack>
);


export const UserTalkingBox = () => {
  return (
    <Stack className="user-msg" direction="column" alignItems="flex-end" gap={1} maxWidth="md"
    >
      <Stack
        direction="column"
        maxWidth="sm"
        alignItems={"center"}
        justifyContent={"center"}
        sx={{
          px: 1, py: 2,
          borderRadius: 5,
          boxShadow: 0,
          minWidth: "100px",
          mt: 0,
          bgcolor: "#EAEAEA",
          color: "dark.main",
          borderTopRightRadius: 0,
          textAlign: "center"
        }}
      >
        <div className="loader"></div>
      </Stack>
    </Stack>
  )
}
